// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  type RefObject,
} from 'react';

import type { ModelEditOp, SolidWall } from '../../api/types';
import { useEditorGesture, type GeometriEditor, type GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { applyOps, lengthInput, parseLength } from '../../bygning/geometriEdit';
import { boxMerge, boxMode, boxSelect, elementsAt, type BoxMode } from '../../bygning/geometriSelect';
import { annotationsAt, annotationsInBox, pickCandidates, pickClick } from '../../bygning/annotationEdit';
import { guideSnaps, spaceTempDims } from '../../bygning/cadMeasure';
import { formatAreaDa, labelAnchor } from '../../bygning/spaces';
import { INFERENCE_TOKEN, infer, manhattanAngle, snapOffset, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import { TRACK_TOKEN, autoTrackPoints, type Track, type TrackPoint } from '../../bygning/smartGuides';
import { useSmartTracking } from './useSmartTracking';
import {
  angleOf,
  dragEndpointOps,
  endingOn,
  frame,
  joinedAt,
  moveWallOps,
  tempDimOps,
  tempDims,
  thicknessOps,
  typedEndpoint,
  wallGrips,
  type GripKind,
  type TempDim,
} from '../../bygning/wallGrips';
import type { ViewportHandle } from '../../viewport/Viewport';
import styles from './GeometriLayer.module.css';

/** A press that moves less than this (px) is a click; more starts a box or a drag. */
const DRAG_PX = 4;
/** Pick slack around a wall's axis, px (on top of its half thickness). */
const PICK_PX = 8;
/** The inference engine's reach, px. */
const SNAP_PX = 12;

export interface GeometriLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  /** The Ground's region: the layer listens on it, ahead of the canvas's camera controls. */
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  /** The storey the plan cuts (null = every storey). */
  storeyId: number | null;
  /** World z of the plane clicks land on (the storey floor). */
  z: number;
}

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  mode: BoxMode;
}

interface Mods {
  shift: boolean;
  /** Ctrl / ⌘: the joined corners stay where they are. */
  free: boolean;
  /** Alt: no snapping. */
  alt: boolean;
}

/** A grip drag (S3): from the press (or a click that armed it) to the commit. */
interface Drag {
  grip: GripKind;
  guid: string;
  /** The wall as it was when the drag began. */
  wall: SolidWall;
  /** Endpoint: the fixed other end; move / thickness: the wall's middle. */
  anchor: XY;
  /** A click without a drag armed it: it follows the pointer until the next click. */
  armed: boolean;
  moved: boolean;
  raw: XY | null;
  mods: Mods;
  /** Shift's locked inference (its line). */
  lock: Inference | null;
  inf: Inference | null;
  /** SketchUp's measurement box: the typed fields (length, angle) and the one being typed. */
  typed: { fields: string[]; at: number } | null;
  ops: ModelEditOp[];
}

const fmt = (v: number) => lengthInput(v);
const fmtAngle = (deg: number) => `${deg.toFixed(1).replace('.', ',')}°`;
const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const GRIP_LABEL: Record<GripKind, string> = { start: 'Endepunkt', end: 'Endepunkt', move: 'Flyt væg', thickness: 'Tykkelse' };
const FIELD_LABEL: Record<GripKind, readonly string[]> = { start: ['Længde', 'Vinkel'], end: ['Længde', 'Vinkel'], move: ['Afstand'], thickness: ['Tykkelse'] };

/**
 * Rediger › Geometri's pointer on the locked plan (frontend-u0o.9, .3), for
 * the Vælg tool:
 *
 *  - a click picks what lies under it (Shift toggles it in the selection;
 *    Tab then cycles what overlaps there) — the guides and dimension
 *    chains too (S9, `annotationEdit.ts`; their grips are AnnotationLayer's);
 *    a left drag on empty plan boxes (left to right a window, right to
 *    left a crossing, dashed);
 *  - a selected wall shows its grips — the endpoints, the move grip at its
 *    middle, the face grip (thickness) — and Revit's blue temporary
 *    dimensions (its length, its distance to the nearest parallel wall each
 *    side), which take a typed value on a click;
 *  - a grip drags with the inference engine (`inference.ts`): a glyph and a
 *    ScreenTip say what it holds to; Shift locks that, Alt lets go of
 *    snapping, Ctrl leaves the joined corners where they are. Digits typed
 *    mid-drag go to the measurement box (Tab: the angle), Enter sets it, Esc
 *    cancels. A press without a drag arms the grip: it follows the pointer
 *    until the next click (SketchUp). Every drag commits as one undoable
 *    batch.
 *
 * It listens on the Ground in the capture phase, so a left press never
 * reaches the camera: the plan pans with the right button and zooms with
 * the wheel, as in a CAD program.
 */
export function GeometriLayer({ viewport, host, editor, storeyId, z }: GeometriLayerProps) {
  const [box, setBox] = useState<Box | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  // Smart guides' tracking points, acquired during an endpoint drag (frontend-u0o.12).
  const tracking = useSmartTracking(drag ? `${drag.guid}:${drag.grip}` : null);
  const [editDim, setEditDim] = useState<TempDim['id'] | null>(null);
  const [tick, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const live = useRef({ editor, storeyId, z });
  live.current = { editor, storeyId, z };

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(bump);
    if (layerRef.current) observer?.observe(layerRef.current);
    bump();
    return () => {
      off();
      observer?.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const canvasRect = () => layerRef.current?.getBoundingClientRect() ?? null;
  const planAtClient = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = canvasRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );
  /** Metres per `px` screen pixels at the plan's zoom. */
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );

  // -------------------------------------------------- inference input --
  const model = editor.model;
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      out.push({ a: w.start, b: w.end, source: 'wall', label: `Væg ${w.id}`, guid: w.guid });
    }
    for (const t of snap?.plane_traces ?? []) {
      out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    }
    return out;
  }, [model.walls, snap, storeyId]);
  // The tape measure's guides (S9) snap every drag too.
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const liveInputs = useRef({ segments, axisAngle, guides });
  liveInputs.current = { segments, axisAngle, guides };

  const setDrag = useCallback((d: Drag | null) => {
    dragRef.current = d;
    setDragState(d);
  }, []);

  /** The drag's ops, inference and readout for pointer `raw` (or the typed values). */
  const evaluate = useCallback(
    (d: Drag, raw: XY | null): Drag => {
      const { editor: ed } = live.current;
      const m = ed.model;
      const walls = m.walls ?? [];
      const w = d.wall;
      const f = frame(w);
      if (!f) return d;
      const keepCorners = !d.mods.free;
      const exclude = new Set<string>([w.guid]);
      if (d.grip === 'start' || d.grip === 'end') {
        if (keepCorners) for (const j of joinedAt(walls, w, d.grip)) exclude.add(j.wall.guid);
      } else if (d.grip === 'move') for (const j of endingOn(walls, w)) exclude.add(j.wall.guid);
      const ctx = {
        segments: [...liveInputs.current.segments, ...liveInputs.current.guides.segments],
        points: liveInputs.current.guides.points,
        axisAngle: liveInputs.current.axisAngle,
        origin: d.grip === 'thickness' ? null : d.anchor,
        toggles: ed.snap,
        tolerance: metresPer(SNAP_PX),
        exclude,
        suspended: d.mods.alt,
        // The wall's own ends as they were and its neighbours' far ends, beside the hovered ones.
        tracked: [...tracking.points(), ...autoTrackPoints(walls, [w.guid], d.anchor)],
      };
      // An endpoint lands on a point; a move or a face snaps by distance (`snapOffset`).
      let inf: Inference | null = d.inf;
      let rawOffset: number | null = null;
      if (raw && (d.grip === 'start' || d.grip === 'end')) {
        inf = infer(raw, ctx);
        tracking.observe(inf);
      }
      else if (raw) {
        const base = d.grip === 'move' ? d.anchor : w.start;
        rawOffset = dot(sub(raw, base), f.normal);
        const snapped = snapOffset(base, f.normal, rawOffset, ctx, d.grip === 'thickness');
        rawOffset = snapped.offset;
        inf = snapped.inference;
      }
      if (raw && d.mods.shift && d.lock && (d.lock.guide || d.lock.segment) && (d.grip === 'start' || d.grip === 'end')) {
        const [a, b] = d.lock.guide ?? [d.lock.segment!.a, d.lock.segment!.b];
        // Shift: stay on the locked line, wherever the pointer goes.
        const dir = sub(b, a);
        const L2 = dot(dir, dir);
        const t = L2 > 0 ? dot(sub(raw, a), dir) / L2 : 0;
        inf = { ...d.lock, point: [a[0] + dir[0] * t, a[1] + dir[1] * t], tip: `${d.lock.tip} · låst` };
      }
      const target = inf?.point ?? raw;
      let ops: ModelEditOp[] = [];
      const typedLen = d.typed ? parseLength(d.typed.fields[0] ?? '') : null;
      const typedAngle = d.typed && d.typed.fields[1] ? Number(d.typed.fields[1].replace(',', '.')) : null;
      if (d.grip === 'start' || d.grip === 'end') {
        let to = target;
        if (typedLen !== null) {
          const dir = target ? sub(target, d.anchor) : sub(w[d.grip], d.anchor);
          to = typedEndpoint(d.anchor, dir, typedLen, typedAngle !== null && Number.isFinite(typedAngle) ? typedAngle : null);
        }
        if (to) ops = dragEndpointOps(m, w.guid, d.grip, to, keepCorners);
      } else if (d.grip === 'move') {
        let offset = rawOffset ?? 0;
        if (typedLen !== null) offset = (offset < 0 ? -1 : 1) * typedLen;
        // The live model's wall may already be the moved one: move from the drag's start.
        ops = moveWallOps({ ...m, walls: walls.map((x) => (x.guid === w.guid ? w : x)) }, w.guid, offset);
      } else {
        let thickness = rawOffset !== null ? Math.abs(rawOffset) * 2 : (w.thickness ?? 0);
        if (typedLen !== null) thickness = typedLen;
        ops = thicknessOps(w, thickness);
      }
      return { ...d, raw: raw ?? d.raw, inf, ops };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metresPer],
  );

  /** The gesture's status line: hint and readout. */
  const status = useCallback((d: Drag): GestureStatus => {
    const after = applyOps(live.current.editor.model, d.ops);
    const w = after.walls?.find((x) => x.guid === d.guid) ?? d.wall;
    let readout = '';
    if (d.grip === 'start' || d.grip === 'end') {
      const p = w[d.grip];
      readout = `Længde ${fmt(dist(d.anchor, p))} · ${fmtAngle(angleOf(d.anchor, p))}`;
    } else if (d.grip === 'move') {
      const f = frame(d.wall);
      readout = `Afstand ${fmt(f ? Math.abs(dot(sub(w.start, d.wall.start), f.normal)) : 0)}`;
    } else readout = `Tykkelse ${fmt(w.thickness ?? 0)}`;
    const keys = [
      { key: '0–9', text: d.grip === 'start' || d.grip === 'end' ? 'skriv længde' : 'skriv mål' },
      ...(d.grip === 'start' || d.grip === 'end' ? [{ key: 'Tab', text: 'vinkel' }] : []),
      { key: '⇧', text: 'lås retning' },
      { key: 'Alt', text: 'uden snap' },
      ...(d.grip === 'start' || d.grip === 'end' ? [{ key: 'Ctrl', text: 'slip hjørnet' }] : []),
      { key: 'Esc', text: 'annullér' },
    ];
    return { hint: { lead: d.armed ? `${GRIP_LABEL[d.grip]} · klik for at sætte` : GRIP_LABEL[d.grip], keys }, readout };
  }, []);

  const update = useCallback(
    (d: Drag | null) => {
      setDrag(d);
      live.current.editor.gesture.set(d ? status(d) : null);
    },
    [setDrag, status],
  );

  const finish = useCallback(
    (d: Drag) => {
      update(null);
      if (d.ops.length === 0) return;
      const label = `${GRIP_LABEL[d.grip]} · Væg ${d.wall.id}`;
      void live.current.editor.commit(d.ops, label);
    },
    [update],
  );

  // --------------------------------------------- the select pointer --
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const canvasXY = (e: PointerEvent, canvas: Element): [number, number] => {
      const r = canvas.getBoundingClientRect();
      return [e.clientX - r.left, e.clientY - r.top];
    };
    const planAt = (x: number, y: number): XY | null => {
      const hit = viewport.current?.planPoint(x, y, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    };

    const onMove = (e: PointerEvent) => {
      if (!(e.target instanceof HTMLCanvasElement)) return;
      const [x, y] = canvasXY(e, e.target);
      live.current.editor.cursor.set(planAt(x, y));
    };
    const onLeave = () => live.current.editor.cursor.set(null);

    const onDown = (e: PointerEvent) => {
      const { editor: ed } = live.current;
      // An armed grip: this click sets it.
      const armed = dragRef.current;
      if (armed?.armed && e.button === 0) {
        e.stopPropagation();
        e.preventDefault();
        finish(armed);
        return;
      }
      // A modify command (S4) takes the plan's clicks itself (CadToolLayer); so does Ny snitlinje (S7).
      if (e.button !== 0 || ed.tool !== 'select' || ed.modify || ed.sectionDraw || !(e.target instanceof HTMLCanvasElement)) return;
      // Ours, not the camera's: a left drag is a box here, never a pan.
      e.stopPropagation();
      const canvas = e.target;
      const [x0, y0] = canvasXY(e, canvas);
      let dragging = false;
      const move = (m: PointerEvent) => {
        const [x1, y1] = canvasXY(m, canvas);
        if (!dragging && Math.hypot(x1 - x0, y1 - y0) < DRAG_PX) return;
        dragging = true;
        setBox({ x0, y0, x1, y1, mode: boxMode(x0, x1) });
      };
      const stop = () => {
        window.removeEventListener('pointermove', move, true);
        window.removeEventListener('pointerup', up, true);
        window.removeEventListener('pointercancel', stop, true);
        setBox(null);
      };
      const up = (u: PointerEvent) => {
        stop();
        const { editor: now, storeyId: sid } = live.current;
        const [x1, y1] = canvasXY(u, canvas);
        if (dragging) {
          const corners = [planAt(x0, y0), planAt(x1, y0), planAt(x1, y1), planAt(x0, y1)];
          if (corners.some((c) => c === null)) return;
          const quad = corners as XY[];
          const picked = boxSelect(now.model, quad, boxMode(x0, x1), sid);
          // Guides and chains too (S9); a guide line only when the box caught nothing else.
          const notes = annotationsInBox(now.annotations, quad, boxMode(x0, x1), sid, picked.length > 0);
          now.select(boxMerge(now.selection, picked, u.shiftKey), boxMerge(now.annotationSelection, notes, u.shiftKey));
          return;
        }
        const p = planAt(x0, y0);
        const near = planAt(x0 + PICK_PX, y0);
        if (!p) return;
        const tol = near ? Math.hypot(near[0] - p[0], near[1] - p[1]) : 0.1;
        // Rooms and slabs (S7) cover the plan: a click takes them after the walls, openings and guides.
        const areas = new Set([...(now.model.spaces ?? []), ...(now.model.slabs ?? [])].map((x) => x.guid));
        const candidates = pickCandidates(now.annotations, annotationsAt(now.annotations, p, tol, sid), elementsAt(now.model, p, tol, sid), (g) => areas.has(g));
        now.setCandidates(candidates.map((c) => c.id));
        const next = pickClick({ elements: now.selection, annotations: now.annotationSelection }, candidates[0] ?? null, u.shiftKey);
        now.select(next.elements, next.annotations);
      };
      window.addEventListener('pointermove', move, true);
      window.addEventListener('pointerup', up, true);
      window.addEventListener('pointercancel', stop, true);
    };

    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      live.current.editor.cursor.set(null);
    };
  }, [host, viewport, finish]);

  // ------------------------------------------------------ grip drags --
  const modsOf = (e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): Mods => ({
    shift: e.shiftKey,
    free: e.ctrlKey || e.metaKey,
    alt: e.altKey,
  });

  const startGrip = (grip: GripKind, w: SolidWall, e: ReactPointerEvent) => {
    if (e.button !== 0 || !editor.canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const f = frame(w);
    if (!f) return;
    const mid: XY = [(w.start[0] + w.end[0]) / 2, (w.start[1] + w.end[1]) / 2];
    const anchor: XY = grip === 'start' ? w.end : grip === 'end' ? w.start : mid;
    const x0 = e.clientX;
    const y0 = e.clientY;
    update({ grip, guid: w.guid, wall: w, anchor, armed: false, moved: false, raw: null, mods: modsOf(e), lock: null, inf: null, typed: null, ops: [] });
    const move = (m: PointerEvent) => {
      const cur = dragRef.current;
      if (!cur) return;
      const moved = cur.moved || Math.hypot(m.clientX - x0, m.clientY - y0) >= DRAG_PX;
      if (!moved) return;
      const mods = modsOf(m);
      const lock = mods.shift ? (cur.lock ?? cur.inf) : null;
      const raw = planAtClient(m.clientX, m.clientY);
      update(evaluate({ ...cur, moved, mods, lock }, raw));
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
    };
    const up = () => {
      const cur = dragRef.current;
      if (!cur) return end();
      if (!cur.moved || cur.typed) {
        // A click arms the grip (or typing holds it): it follows the pointer until the next click or Enter.
        window.removeEventListener('pointerup', up, true);
        update({ ...cur, armed: true });
        return;
      }
      end();
      finish(cur);
    };
    const cancel = () => {
      end();
      update(null);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
  };

  // Keys while a drag lasts: the measurement box, Tab, Enter, Esc and the modifiers.
  const dragOn = drag !== null;
  useEffect(() => {
    if (!dragOn) return;
    const onKey = (e: KeyboardEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const fields = FIELD_LABEL[d.grip];
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (d.typed) update(evaluate({ ...d, typed: null }, d.raw));
        else update(null);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        finish(evaluate(d, d.raw));
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        if (fields.length > 1) {
          const typed = d.typed ?? { fields: [], at: 0 };
          update(evaluate({ ...d, typed: { ...typed, at: (typed.at + (e.shiftKey ? fields.length - 1 : 1)) % fields.length } }, d.raw));
        }
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        if (!d.typed) return;
        const next = [...d.typed.fields];
        next[d.typed.at] = (next[d.typed.at] ?? '').slice(0, -1);
        update(evaluate({ ...d, typed: next.every((t) => !t) ? null : { ...d.typed, fields: next } }, d.raw));
        return;
      }
      if (/^[0-9.,-]$/.test(e.key) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        const typed = d.typed ?? { fields: [], at: 0 };
        const next = [...typed.fields];
        next[typed.at] = (next[typed.at] ?? '') + e.key;
        update(evaluate({ ...d, armed: true, typed: { ...typed, fields: next } }, d.raw));
        return;
      }
      if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta' || e.key === 'Alt') {
        const mods = { shift: e.shiftKey, free: e.ctrlKey || e.metaKey, alt: e.altKey };
        update(evaluate({ ...d, mods, lock: e.shiftKey ? (d.lock ?? d.inf) : null }, d.raw));
        // Every other key is swallowed while dragging: no tool switch mid-drag.
      }
      if (e.key.length === 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const d = dragRef.current;
      if (!d || !['Shift', 'Control', 'Meta', 'Alt'].includes(e.key)) return;
      const mods = { shift: e.shiftKey, free: e.ctrlKey || e.metaKey, alt: e.altKey };
      update(evaluate({ ...d, mods, lock: e.shiftKey ? d.lock : null }, d.raw));
    };
    // An armed grip follows the pointer with no button held.
    const onMove = (m: PointerEvent) => {
      const d = dragRef.current;
      if (!d || !d.armed || m.buttons !== 0) return;
      const mods = modsOf(m);
      update(evaluate({ ...d, moved: true, mods, lock: mods.shift ? (d.lock ?? d.inf) : null }, planAtClient(m.clientX, m.clientY)));
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('pointermove', onMove, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('pointermove', onMove, true);
    };
  }, [dragOn, evaluate, finish, update, planAtClient]);

  // A selection change or leaving the plan drops a drag.
  const selectedGuid = editor.selection.length === 1 ? editor.selection[0] : null;
  useEffect(() => {
    const d = dragRef.current;
    if (d && d.guid !== selectedGuid) update(null);
    setEditDim(null);
  }, [selectedGuid, update]);
  useEffect(() => () => live.current.editor.gesture.set(null), []);

  // ------------------------------------------------------------ draw --
  void tick; // the projection reads the camera
  const selectedWall = useMemo(
    () => (selectedGuid ? ((model.walls ?? []).find((w) => w.guid === selectedGuid && (storeyId === null || w.storey_id === storeyId)) ?? null) : null),
    [model.walls, selectedGuid, storeyId],
  );
  const preview = useMemo(() => (drag ? applyOps(model, drag.ops) : null), [drag, model]);
  const modifying = editor.modify !== null || editor.tool !== 'select';
  const dims = useMemo(() => (selectedWall && !drag && !modifying ? tempDims(model, selectedWall.guid) : []), [selectedWall, drag, model, modifying]);
  // A selected room's temporary dimensions (S9): its edges, set just outside, and its area.
  const selectedSpace = useMemo(
    () => (selectedGuid && !modifying ? ((model.spaces ?? []).find((x) => x.guid === selectedGuid && (storeyId === null || x.storey_id === storeyId) && x.polygon.outer.length >= 3) ?? null) : null),
    [model.spaces, selectedGuid, storeyId, modifying],
  );
  // Outside the room, over its walls: inside, a narrow room's figures would sit on its own label;
  // far enough out to clear the edge grips (RoomLayer, S6).
  const roomInset = -metresPer(editor.canEdit ? 26 : 16);
  // A room's grip drag (RoomLayer, S6) shows its own outline and area: the old figures go meanwhile.
  const gestureOn = useEditorGesture(editor) !== null;
  const roomDims = selectedSpace && !gestureOn ? spaceTempDims(selectedSpace, roomInset, metresPer(36)) : null;
  const roomAnchor = useMemo(() => (selectedSpace ? labelAnchor(selectedSpace.polygon) : null), [selectedSpace]);
  const canEdit = editor.canEdit;

  const poly = (pts: XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const outline = (w: SolidWall): string | null => {
    const f = frame(w);
    if (!f) return null;
    const h = Math.max((w.thickness ?? 0) / 2, 0.01);
    const n: XY = [f.normal[0] * h, f.normal[1] * h];
    return poly([
      [w.start[0] + n[0], w.start[1] + n[1]],
      [w.end[0] + n[0], w.end[1] + n[1]],
      [w.end[0] - n[0], w.end[1] - n[1]],
      [w.start[0] - n[0], w.start[1] - n[1]],
    ]);
  };

  const changed = preview && drag ? (preview.walls ?? []).filter((w) => drag.ops.some((o) => 'guid' in o && o.guid === w.guid)) : [];
  const shownWall = drag ? (preview?.walls?.find((w) => w.guid === drag.guid) ?? drag.wall) : selectedWall;
  const grips = shownWall && canEdit && editor.tool === 'select' && !editor.modify ? wallGrips(shownWall) : [];

  // A typed value overrides the pointer: no inference is shown for it.
  const shownInf = drag && !drag.typed ? drag.inf : null;
  const infAt = shownInf && shownInf.kind !== 'free' ? toScreen(shownInf.point) : null;
  const guide = shownInf?.guide ? poly([shownInf.guide[0], shownInf.guide[1]]) : null;
  const pointerAt = drag?.raw ? toScreen(drag.inf?.point ?? drag.raw) : null;

  // The live dimension: the dragged wall's length, or the move's offset.
  let liveDim: { at: [number, number]; text: string } | null = null;
  if (drag && shownWall) {
    const mid = toScreen([(shownWall.start[0] + shownWall.end[0]) / 2, (shownWall.start[1] + shownWall.end[1]) / 2]);
    if (mid) {
      const f = frame(drag.wall);
      const text =
        drag.grip === 'start' || drag.grip === 'end'
          ? fmt(dist(shownWall.start, shownWall.end))
          : drag.grip === 'move'
            ? fmt(f ? Math.abs(dot(sub(shownWall.start, drag.wall.start), f.normal)) : 0)
            : fmt(shownWall.thickness ?? 0);
      liveDim = { at: mid, text };
    }
  }

  const boxLeft = box ? Math.min(box.x0, box.x1) : 0;
  const boxTop = box ? Math.min(box.y0, box.y1) : 0;

  // The wheel zooms the plan wherever the pointer is: over a grip or a dimension's figure too.
  const forwardWheel = (e: ReactWheelEvent) => {
    const canvas = host.current?.querySelector('canvas');
    if (!canvas || e.target === canvas) return;
    canvas.dispatchEvent(new WheelEvent('wheel', { ...e.nativeEvent, deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, bubbles: true, cancelable: true }));
  };

  return (
    <div ref={layerRef} className={styles.layer} onWheel={forwardWheel}>
      {/* Grips are the pointer's way in; the keyboard's is Egenskaber and the dimensions' figures. */}
      <svg className={styles.svg} aria-hidden="true">
        {/* The guide the inference holds to (an axis from the origin, a trace). */}
        {guide && shownInf && (
          <polyline
            points={guide}
            className={shownInf.kind === 'trace' ? `${styles.guide} ${styles.traceGuide}` : styles.guide}
            style={{ stroke: `var(${INFERENCE_TOKEN[shownInf.kind]})` }}
          />
        )}
        {/* Smart guides: their dotted lines and the acquired tracking points. */}
        {drag && <TrackLines tracks={shownInf?.tracks} points={tracking.tracked} toScreen={toScreen} />}
        {/* The walls a drag moves: their new outline in the accent. */}
        {changed.map((w) => {
          const d = outline(w);
          return d ? <polygon key={w.guid} points={d} className={styles.preview} /> : null;
        })}
        {/* Temporary dimensions (Revit): blue, click a figure to type. */}
        {dims.map((dim) => {
          const a = toScreen(dim.from);
          const b = toScreen(dim.to);
          if (!a || !b) return null;
          const dx = b[0] - a[0];
          const dy = b[1] - a[1];
          const L = Math.hypot(dx, dy) || 1;
          const nx = (-dy / L) * 4;
          const ny = (dx / L) * 4;
          return (
            <g key={dim.id} className={styles.dim}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
              <line x1={a[0] - nx} y1={a[1] - ny} x2={a[0] + nx} y2={a[1] + ny} />
              <line x1={b[0] - nx} y1={b[1] - ny} x2={b[0] + nx} y2={b[1] + ny} />
            </g>
          );
        })}
        {roomDims?.edges.map((e, i) => {
          const a = toScreen(e.from);
          const b = toScreen(e.to);
          if (!a || !b) return null;
          const dx = b[0] - a[0];
          const dy = b[1] - a[1];
          const L = Math.hypot(dx, dy) || 1;
          const nx = (-dy / L) * 4;
          const ny = (dx / L) * 4;
          return (
            <g key={`r${i}`} className={styles.dim}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
              <line x1={a[0] - nx} y1={a[1] - ny} x2={a[0] + nx} y2={a[1] + ny} />
              <line x1={b[0] - nx} y1={b[1] - ny} x2={b[0] + nx} y2={b[1] + ny} />
            </g>
          );
        })}
        {/* Grips: endpoints (circles), the move grip (a diamond with its arrows), the face (a square). */}
        {shownWall &&
          grips.map((g) => {
            const s = toScreen(g.at);
            if (!s) return null;
            const on = drag?.grip === g.kind;
            const common = {
              className: on ? `${styles.grip} ${styles.gripOn}` : styles.grip,
              onPointerDown: (e: ReactPointerEvent) => startGrip(g.kind, shownWall, e),
            };
            if (g.kind === 'start' || g.kind === 'end') {
              return <circle key={g.kind} cx={s[0]} cy={s[1]} r={6} {...common}><title>Træk endepunktet</title></circle>;
            }
            if (g.kind === 'thickness') {
              const q = toScreen([g.at[0] + (shownWall.end[0] - shownWall.start[0]) * 0.25, g.at[1] + (shownWall.end[1] - shownWall.start[1]) * 0.25]);
              if (!q) return null;
              return (
                <rect key={g.kind} x={q[0] - 4.5} y={q[1] - 4.5} width={9} height={9} {...common}>
                  <title>Træk fladen: tykkelse</title>
                </rect>
              );
            }
            const n = g.normal!;
            const up = toScreen([g.at[0] + n[0], g.at[1] + n[1]]);
            const ux = up ? up[0] - s[0] : 0;
            const uy = up ? up[1] - s[1] : -1;
            const ul = Math.hypot(ux, uy) || 1;
            const ax = (ux / ul) * 18;
            const ay = (uy / ul) * 18;
            return (
              <g key={g.kind}>
                <path className={styles.arrow} d={`M${s[0] + ax * 0.55} ${s[1] + ay * 0.55}L${s[0] + ax} ${s[1] + ay}M${s[0] - ax * 0.55} ${s[1] - ay * 0.55}L${s[0] - ax} ${s[1] - ay}`} />
                <rect x={s[0] - 5} y={s[1] - 5} width={10} height={10} transform={`rotate(45 ${s[0]} ${s[1]})`} {...common}>
                  <title>Træk for at flytte væggen vinkelret</title>
                </rect>
              </g>
            );
          })}
        {/* The inference glyph. */}
        {infAt && shownInf && <Glyph kind={shownInf.kind} at={infAt} />}
      </svg>
      {box && (
        <div
          className={box.mode === 'window' ? styles.window : `${styles.window} ${styles.crossing}`}
          style={{ transform: `translate(${boxLeft}px, ${boxTop}px)`, width: Math.abs(box.x1 - box.x0), height: Math.abs(box.y1 - box.y0) }}
        />
      )}
      {/* Temporary dimension figures: buttons, an input once clicked. */}
      {dims.map((dim) => {
        const at = toScreen(dim.at);
        if (!at) return null;
        const style = { transform: `translate(${at[0].toFixed(1)}px, ${at[1].toFixed(1)}px) translate(-50%, -50%)` };
        if (editDim === dim.id && selectedWall) {
          return (
            <DimInput
              key={dim.id}
              style={style}
              value={dim.value}
              label={dim.id === 'length' ? 'Væggens længde' : 'Afstand til nabovæggen'}
              onCancel={() => setEditDim(null)}
              onCommit={(value) => {
                setEditDim(null);
                const ops = tempDimOps(editor.model, selectedWall.guid, dim, value);
                if (ops.length > 0) void editor.commit(ops, `Mål · Væg ${selectedWall.id}`);
              }}
            />
          );
        }
        return (
          <button
            key={dim.id}
            type="button"
            className={styles.dimLabel}
            style={style}
            disabled={!canEdit}
            title={dim.id === 'length' ? 'Væggens længde — klik for at skrive' : 'Afstand til nabovæggen — klik for at skrive'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setEditDim(dim.id)}
          >
            {fmt(dim.value)}
          </button>
        );
      })}
      {roomDims?.edges.map((e, i) => {
        const at = toScreen(e.at);
        return at ? (
          <span key={`r${i}`} className={styles.dimStatic} style={{ transform: `translate(${at[0].toFixed(1)}px, ${at[1].toFixed(1)}px) translate(-50%, -50%)` }}>
            {fmt(e.value)}
          </span>
        ) : null;
      })}
      {roomDims && roomAnchor && (() => {
        const at = toScreen([roomAnchor.x, roomAnchor.y]);
        return at ? (
          // Under the room's name (PlanLayer's label), never over it.
          <span className={styles.roomArea} style={{ transform: `translate(${at[0].toFixed(1)}px, ${(at[1] + 22).toFixed(1)}px) translate(-50%, 0)` }}>
            {formatAreaDa(roomDims.area)} · omkreds {fmt(roomDims.perimeter)} m
          </span>
        ) : null;
      })()}
      {liveDim && (
        <span className={styles.liveDim} style={{ transform: `translate(${liveDim.at[0].toFixed(1)}px, ${liveDim.at[1].toFixed(1)}px) translate(-50%, -50%)` }}>
          {liveDim.text}
        </span>
      )}
      {/* The ScreenTip. */}
      {infAt && shownInf?.tip && <ScreenTip inf={shownInf} at={infAt} />}
      {/* The measurement box at the cursor (SketchUp). */}
      {drag && pointerAt && drag.moved && <MeasureBox drag={drag} at={pointerAt} live={liveValues(drag, shownWall)} />}
    </div>
  );
}

/** The drag's live values per measurement-box field (length / angle, offset, thickness), as typed text would read. */
function liveValues(drag: Drag, w: SolidWall | null): string[] {
  if (!w) return [];
  if (drag.grip === 'start' || drag.grip === 'end') {
    const p = w[drag.grip];
    return [fmt(dist(drag.anchor, p)), angleOf(drag.anchor, p).toFixed(1).replace('.', ',')];
  }
  if (drag.grip === 'move') {
    const f = frame(drag.wall);
    return [fmt(f ? Math.abs(dot(sub(w.start, drag.wall.start), f.normal)) : 0)];
  }
  return [fmt(w.thickness ?? 0)];
}

/**
 * Smart guides' dotted lines (frontend-u0o.12), each in its colour — the
 * axes red / green, along / square magenta, an extension cyan — with a
 * small square where a line starts from a point, and a cross on each
 * hover-acquired tracking point.
 */
export function TrackLines({ tracks, points, toScreen }: { tracks?: readonly Track[] | null; points: readonly TrackPoint[]; toScreen: (p: XY) => [number, number] | null }) {
  return (
    <g>
      {tracks?.map((t, i) => {
        const a = toScreen(t.a);
        const b = toScreen(t.b);
        if (!a || !b) return null;
        const style = { stroke: `var(${TRACK_TOKEN[t.kind]})` };
        return (
          <g key={i}>
            <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className={t.kind === 'length' ? `${styles.track} ${styles.trackRef}` : styles.track} style={style} />
            {t.mark && <rect x={a[0] - 3} y={a[1] - 3} width={6} height={6} className={styles.trackFrom} style={style} />}
          </g>
        );
      })}
      {points.map((q) => {
        const s = toScreen(q.p);
        return s ? <path key={`${q.p[0]},${q.p[1]}`} className={styles.trackPoint} d={`M${s[0] - 5} ${s[1]}H${s[0] + 5}M${s[0]} ${s[1] - 5}V${s[1] + 5}`} /> : null;
      })}
    </g>
  );
}

/** SketchUp's glyph per inference kind: endpoint a square, midpoint a circle, intersection an ×, on-edge a diamond. */
export function Glyph({ kind, at }: { kind: Inference['kind']; at: [number, number] }) {
  const [x, y] = at;
  const style = { stroke: `var(${INFERENCE_TOKEN[kind]})` };
  switch (kind) {
    case 'endpoint':
    case 'guide-point':
      return <rect className={styles.glyph} style={style} x={x - 5} y={y - 5} width={10} height={10} />;
    case 'midpoint':
      return <circle className={styles.glyph} style={style} cx={x} cy={y} r={5.5} />;
    case 'intersection':
      return <path className={styles.glyph} style={style} d={`M${x - 5} ${y - 5}L${x + 5} ${y + 5}M${x + 5} ${y - 5}L${x - 5} ${y + 5}`} />;
    case 'on-edge':
    case 'trace':
    case 'guide':
      return <rect className={styles.glyph} style={style} x={x - 4.5} y={y - 4.5} width={9} height={9} transform={`rotate(45 ${x} ${y})`} />;
    case 'perpendicular':
      return <path className={styles.glyph} style={style} d={`M${x - 6} ${y + 5}H${x + 6}M${x} ${y + 5}V${y - 7}`} />;
    case 'track':
      return <path className={styles.glyph} style={style} d={`M${x - 6} ${y}H${x + 6}M${x} ${y - 6}V${y + 6}`} />;
    default:
      return <circle className={styles.glyph} style={style} cx={x} cy={y} r={3.5} />;
  }
}

/** The tip beside the glyph: "På flade · Væg 5 · på rød akse", the axis part in its colour. */
export function ScreenTip({ inf, at }: { inf: Inference; at: [number, number] }) {
  // Only an axis gets its colour: "· på flade · Væg 5" (a smart guide on an edge) reads plain.
  const cut = inf.axis ? inf.tip.lastIndexOf(' · på ') : -1;
  const head = cut >= 0 ? inf.tip.slice(0, cut) : inf.tip;
  const tail = cut >= 0 ? inf.tip.slice(cut + 3) : null;
  return (
    <span className={styles.tip} style={{ transform: `translate(${(at[0] + 12).toFixed(1)}px, ${(at[1] - 30).toFixed(1)}px)` }}>
      <i style={{ background: `var(${INFERENCE_TOKEN[inf.kind]})` }} aria-hidden="true" />
      {head}
      {tail && (
        <em style={{ color: `var(${inf.axis === 'y' ? '--snap-axis-y' : '--snap-axis-x'})` }}>
          {' · '}
          {tail}
        </em>
      )}
    </span>
  );
}

/** The measurement box at the cursor: the typed value (or the live one), and how to go on. */
function MeasureBox({ drag, at, live }: { drag: Drag; at: [number, number]; live: string[] }) {
  const fields = FIELD_LABEL[drag.grip];
  const field = drag.typed?.at ?? 0;
  const text = drag.typed?.fields[field];
  return (
    <span className={styles.measure} style={{ transform: `translate(${(at[0] + 16).toFixed(1)}px, ${(at[1] + 16).toFixed(1)}px)` }} role="status">
      <span className={styles.measureBox}>
        <small>{fields[field]}</small>
        <b>
          {/* The live value until a digit is typed; the caret marks where typing goes. */}
          {text !== undefined && text !== '' ? text : <span className={styles.measureLive}>{live[field] ?? ''}</span>}
          <u aria-hidden="true" />
          {field === 0 ? ' m' : '°'}
        </b>
      </span>
      <span className={styles.measureCap}>
        Skriv et tal{fields.length > 1 ? ' · Tab ' + fields[(field + 1) % fields.length].toLowerCase() : ''} · ↵ sæt
      </span>
    </span>
  );
}

/** A temporary dimension being typed: Enter sets it, Esc or leaving it drops it. */
export function DimInput({ value, label, style, onCommit, onCancel }: { value: number; label: string; style: CSSProperties; onCommit: (v: number) => void; onCancel: () => void }) {
  const [text, setText] = useState(lengthInput(value));
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const v = parseLength(text);
      if (v !== null) onCommit(v);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };
  return (
    <input
      ref={ref}
      className={styles.dimInput}
      style={style}
      value={text}
      inputMode="decimal"
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onCancel}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}
